import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SvgIcon } from '@progress/kendo-react-common';
import {
    TextBoxHandle,
    TextBox as KendoTextBox,
    TextBoxProps as KendoTextBoxProps,
    InputSuffix,
    TextBoxChangeEvent,
} from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { xIcon } from '@progress/kendo-svg-icons';
import cn from 'clsx';
import { useClipboard } from 'use-clipboard-copy';
import styles from './styles.module.scss';
import { CharactersValidationHint } from '@components-clean/core/characters-validation-hint';
import svgStyles from '@components-clean/core/drag-drop-lists-core/style.module.scss';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import InformationIcon from '@components-clean/core/icons/information-icon';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import ValidationIconSuffix from 'views-clean/components/core/icons/validation-icon-suffix';
import { useTranslation } from '@hooks/index';
import NotificationService from '@utils/notification';
import { validateLimitExceeded } from '@utils/validation';

interface TextBoxProps extends KendoTextBoxProps {
    'data-testid': string;
    validationMessage?: string;
    label?: string;
    inline?: boolean;
    hideClear?: boolean;
    hintText?: string;
    copyBtn?: boolean;
    inactiveValue?: boolean;
    ignoreReadonlyContext?: boolean;
    type?: 'default' | 'card-header';
    suffix?: ReactElement;
}

const TextBox = ({
    children,
    placeholder,
    valid,
    validationMessage,
    label,
    inline,
    value,
    onChange,
    hideClear,
    maxLength,
    'data-testid': testId,
    readOnly,
    hintText,
    copyBtn,
    fillMode,
    disabled,
    inactiveValue,
    className,
    ignoreReadonlyContext,
    type,
    suffix,
    ...props
}: TextBoxProps) => {
    const { t } = useTranslation();
    const clipboard = useClipboard();
    const { readOnly: readonlyCtx } = useReadOnlyContext();
    const [inputValue, setInputValue] = useState<string | number | readonly string[] | undefined>(value);

    const ref = useRef<TextBoxHandle>(null);

    const copyToClipboard = () => {
        clipboard.copy(value);
        NotificationService.showNotification(t('copiedToClipboard'));
    };

    useEffect(() => {
        ref.current?.element?.setAttribute('data-testid', testId);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setInputValue(value);
    }, [value]);

    const isReadOnly = readOnly || (readonlyCtx && !ignoreReadonlyContext);
    const placeholderText = useMemo(() => {
        if (placeholder !== undefined) return placeholder;

        if (isReadOnly) return '';

        return t('cm2EnterValue');
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [placeholder, isReadOnly]);

    const suffixIconGroupClasses = cn('k-display-flex', 'k-align-items-center', {
        [styles.suffixGroupReadOnly]: isReadOnly,
    });

    const classes = cn(className, {
        'opra-inactive-state': inactiveValue,
        'card-header-textbox': type === 'card-header',
    });

    return (
        <div
            className={cn('text-box', styles.textBox, {
                'k-flex-grow': !props.style?.width,
                'k-d-flex k-gap-4 k-align-items-center': inline,
            })}
        >
            <Label
                editorRef={ref}
                className={cn('k-d-flex k-align-items-end', { '!k-pb-0 k-white-space-nowrap': inline })}
            >
                {label}
                {hintText && <InformationIcon title={hintText} className="k-ml-1.5" dataTestId={`${testId}-hint`} />}
            </Label>
            <KendoTextBox
                {...props}
                className={classes}
                ref={ref}
                placeholder={!disabled ? placeholderText : ''}
                valid={valid}
                readOnly={isReadOnly}
                value={inputValue}
                fillMode={fillMode}
                disabled={disabled}
                onChange={(event) => {
                    setInputValue(event.target.value);
                    if (onChange) onChange(event);
                }}
                suffix={() => (
                    <div className="k-display-flex k-align-items-center">
                        {(valid === false || inactiveValue || suffix) && (
                            <div className={suffixIconGroupClasses}>
                                {valid === false && <ValidationIconSuffix validationMessage={validationMessage} />}
                                {inactiveValue && (
                                    <InputSuffix>
                                        <InactiveIcon
                                            dataTestId={`${testId}-inactive`}
                                            isSuffix
                                            tooltipPosition="right"
                                        />
                                    </InputSuffix>
                                )}
                                {!!suffix && <InputSuffix>{suffix}</InputSuffix>}
                            </div>
                        )}
                        {!!value && !isReadOnly && !hideClear && (
                            <InputSuffix
                                data-testid={`${testId}-clear`}
                                onClick={() => {
                                    setInputValue('');
                                    if (onChange) {
                                        onChange({ target: { value: '' } } as TextBoxChangeEvent);
                                    }
                                }}
                            >
                                <SvgIcon icon={xIcon} size="large" className={svgStyles.svgClear} />
                            </InputSuffix>
                        )}
                        {copyBtn && (fillMode === undefined || fillMode === 'solid') && (
                            <InputSuffix
                                data-testid={`${testId}-copy`}
                                onClick={copyToClipboard}
                                className={cn(
                                    'k-d-flex k-justify-content-center k-w-8 k-h-full k-cursor-pointer opra-copy-btn',
                                    styles.copyBtn,
                                    { [styles.copyBtnError]: valid === false },
                                )}
                            >
                                <FontAwesomeIcon icon={['far', 'copy']} fontSize={18} className={styles.copyIcon} />
                            </InputSuffix>
                        )}
                    </div>
                )}
            />
            {maxLength && <CharactersValidationHint length={(inputValue as string)?.length || 0} max={maxLength} />}
        </div>
    );
};

interface TextBoxControllerProps<TData, TControl extends FieldValues> extends TextBoxProps {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean | undefined>;
    handleInputChange?: (event: TextBoxChangeEvent) => void;
}

const TextBoxController = <TData, TControl extends FieldValues>({
    control,
    name,
    disabled,
    'data-testid': dataTestId,
    required = false,
    label,
    maxLength,
    copyBtn,
    fillMode,
    readOnly,
    validateRules,
    handleInputChange,
    ...props
}: TextBoxControllerProps<TData, TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    maxLength: (value) => {
                        if (maxLength) {
                            return validateLimitExceeded(value as string, maxLength);
                        }
                        return true;
                    },
                    ...validateRules,
                },
            }}
            render={({ field: { onChange, value, name: fieldName }, fieldState: { error } }) => {
                return (
                    <TextBox
                        {...props}
                        name={fieldName}
                        data-testid={dataTestId}
                        label={label}
                        disabled={disabled}
                        valid={!error}
                        value={value as string}
                        validationMessage={error?.message}
                        onChange={(event) => {
                            onChange(event.target.value);
                            if (handleInputChange) handleInputChange(event);
                        }}
                        copyBtn={copyBtn}
                        fillMode={fillMode}
                        readOnly={readOnly}
                        maxLength={maxLength}
                    />
                );
            }}
        />
    );
};

export { TextBox, TextBoxController };
